// SweepFailureDetailPopup.tsx
import { Box, Dialog, IconButton, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { makeStyles } from 'tss-react/mui';
import CommonButton from './buttons/CommonButton';
import { useEffect, useState } from 'react';
import Loader from './Loader';
import { getAutoSweepTransactionDetails } from '../api/bankingApi/bankingApi';
import useSnackBar from '../hooks/useSnackBar';
import { AutoSweepTransactionDetails } from '../views/FirmBankingView/types';
import moment from 'moment';

const useStyles = makeStyles()((theme) => ({
  dialogPaper: {
    borderRadius: 16,
    width: '600px',
    backgroundColor: theme.palette.primary.light,
    color: theme.palette.common.white,
    padding: '24px 28px',
    boxShadow: theme.shadows[8],
    fontFamily: 'Poppins',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  title: {
    fontFamily: 'Poppins',
    fontWeight: 600,
    fontSize: '24px',
    color: theme.palette.common.white,
  },
  closeIcon: {
    color: theme.palette.text.disabled,
    padding: 0,
    '&:hover': { opacity: 0.7 },
  },
  amountText: {
    marginTop: '14px',
    fontFamily: 'Poppins',
    fontSize: '16px',
    color: theme.palette.text.disabled,
  },
  sectionLabel: {
    marginTop: '20px',
    fontFamily: 'Poppins',
    fontSize: '14px',
    color: theme.palette.text.disabled,
  },
  failureReason: {
    marginTop: '10px',
    fontFamily: 'Poppins',
    fontSize: '16px',
    fontWeight: 600,
    color: theme.palette.error.main,
  },
  historyWrapper: {
    marginTop: '12px',
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
  },
  historyItem: {
    border: `1px solid ${theme.palette.secondary.dark}`,
    borderRadius: '8px',
    padding: '12px',
    minHeight: '52px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  historyDate: {
    fontFamily: 'Poppins',
    fontSize: '16px',
    fontWeight: 600,
    color: theme.palette.common.white,
  },
  historyDescription: {
    marginTop: '4px',
    fontFamily: 'Poppins',
    fontSize: '15px',
    color: theme.palette.text.disabled,
  },
  statusChip: {
    padding: '6px 16px',
    borderRadius: '20px',
    fontFamily: 'Poppins',
    fontSize: '14px',
    fontWeight: 600,
    whiteSpace: 'nowrap',
  },
  processingChip: {
    backgroundColor: '#FFF1C7',
    color: theme.palette.warning.main,
  },
  failedChip: {
    backgroundColor: '#FFDCDC',
    color: theme.palette.error.main,
  },
  actionWrapper: {
    marginTop: '26px',
  },
}));

// interface AttemptHistoryItem {
//   dateTime: string;
//   status: 'Processing' | 'Failed';
//   description?: string;
// }

interface SweepFailureDetailPopupProps {
  open: boolean;
  onClose: () => void;
  firmId?: number;
  transactionId?: number;
  // attemptHistory: AttemptHistoryItem[];
  onRetrySweep: () => void;
}

const SweepFailureDetailPopup = ({
  open,
  onClose,
  firmId,
  transactionId,
  // attemptHistory,
  onRetrySweep,
}: SweepFailureDetailPopupProps) => {
  const { classes } = useStyles();
  const { setAlert } = useSnackBar();
  const [transactionDetails, setTransactionDetails] = useState<AutoSweepTransactionDetails | undefined>();
  const [isLoading, setIsLoading] = useState(false);

  const getTransactionDetails = (transactionId: number) => {
    if (!firmId) {
      setAlert((prev) => ({
        ...prev,
        message: 'Firm ID is required to load transaction details.',
        type: 'error',
      }));
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    getAutoSweepTransactionDetails(firmId, transactionId)
      .then((res) => {
        setTransactionDetails(res.data);
      })
      .catch((error) => {
        setAlert((prev) => ({
          ...prev,
          message:
            error.response?.data?.message || 'Failed to load transactions.',
          type: 'error',
        }));
      })
      .finally(() => setIsLoading(false));
  };

  useEffect(() => {
    if (transactionId != null && firmId) {
      getTransactionDetails(transactionId);
    } else {
      setTransactionDetails(undefined);
    }
  }, [firmId, transactionId]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      classes={{ paper: classes.dialogPaper }}
      maxWidth={false}
    >
      <Box className={classes.header}>
        <Typography className={classes.title}>Sweep Failure Details</Typography>

        <IconButton onClick={onClose} className={classes.closeIcon}>
          <CloseIcon />
        </IconButton>
      </Box>

      {isLoading ? (
        <Box>
          <Loader colorType="warning" size={48} />
        </Box>
      ) : (
        <>
          <Typography className={classes.amountText}>
            {transactionDetails?.amount} · {moment(transactionDetails?.updatedAt).format('M/D/YYYY, h:mm:ss A')}
          </Typography>

          <Typography className={classes.sectionLabel}>Failure reason</Typography>

          <Typography className={classes.failureReason}>
            {transactionDetails?.errorMessage || ''}
          </Typography>
        </>
      )}

      {/* <Typography className={classes.sectionLabel}>Attempt history</Typography>

      <Box className={classes.historyWrapper}>
        {attemptHistory.map((item) => (
          <Box key={`${item.dateTime}-${item.status}`} className={classes.historyItem}>
            <Box>
              <Typography className={classes.historyDate}>
                {item.dateTime}
              </Typography>

              {item.description && (
                <Typography className={classes.historyDescription}>
                  {item.description}
                </Typography>
              )}
            </Box>

            <Box
              className={`${classes.statusChip} ${
                item.status === 'Processing'
                  ? classes.processingChip
                  : classes.failedChip
              }`}
            >
              {item.status}
            </Box>
          </Box>
        ))}
      </Box> */}

      <Box className={classes.actionWrapper}>
        <CommonButton
          type="infoMain"
          buttonText="Retry sweep"
          onButtonClick={onRetrySweep}
        />
      </Box>
    </Dialog>
  );
};

export default SweepFailureDetailPopup;